import omit from 'lodash/omit';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { API, post } from 'utils/api';
import { cleanSocialHandle, isInstagramUrl } from 'utils/url';

import type {
  ChannelForm,
  ChannelValidationResult,
  ChannelsValidationResult,
  ThunkAction
} from 'types';

export function validateChannelUrl(channelUrl: string): Promise<null | ChannelValidationResult> {
  const url = API.END_POINTS.CHANNEL.validateChannelUrl();

  if (!channelUrl) {
    return Promise.resolve(null);
  }

  return post(url, { data: { url: cleanSocialHandle(channelUrl) } })
    .then(response => response.data)
    .catch(() => null);
}

export function validateChannels(channels: Array<ChannelForm>): ThunkAction {
  return (dispatch): Promise<ChannelsValidationResult> => {
    if (channels.length === 0) {
      return Promise.resolve({ channels, instagramExternalId: null });
    }

    const url = 'validateChannels()';

    dispatch(showLoading(url));

    return new Promise((resolve, reject) => {
      Promise.all(channels.map(channel => validateChannelUrl(channel.url)))
        .then(channelValidationResults => {
          const channelsWithValidationStatus = channels.map((channel, index) => ({
            ...channel,
            isInvalid: channel.url ? channelValidationResults[index] === null : false
          }));
          const instagramExternalId = channels.reduce((acc, ch, indx) => {
            if (acc !== null) {
              return acc;
            }

            if (isInstagramUrl(ch.url) && channelValidationResults[indx] !== null) {
              return channelValidationResults[indx]?.externalId ?? null;
            }

            return null;
          }, null as null | string);

          dispatch(hideLoading(url));

          const hasInvalidChannels = channelsWithValidationStatus
            .some(channel => channel.isInvalid);

          if (hasInvalidChannels) {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({
              channels: channelsWithValidationStatus,
              instagramExternalId
            });
            return;
          }

          resolve({
            channels: channelsWithValidationStatus.map(ch => omit(ch, ['isInvalid'])),
            instagramExternalId
          });
        });
    });
  };
}
